/**
 * Campaign report pages API client — the Ad Ops "Campaign Reports" tab.
 * Backend: /api/ad-ops/campaign-reports (requires adOps.campaignReports.manage).
 *
 * Pages are passcode-gated client reports at
 * culturegenesis.com/campaigns/{slug}/report, built from GAM order data and
 * expiring 60 days after the campaign flight ends. A page may be listed on an
 * advertiser hub (advertiserReportsApi) via `advertiserPageSlug`.
 */

import { apiRequest } from './api';
import type { GamPacingStatus } from './currentCampaignsApi';

const BASE = '/api/ad-ops/campaign-reports';

/** 'pending' = the reporting window has no complete network day yet (flight starts today). */
export type CampaignReportDataStatus = 'generating' | 'pending' | 'ready' | 'error';
export type ReportWindowMode = 'follow_campaign' | 'fixed';
export interface RefreshSectionHealth {
  lastAttemptAt?: string;
  lastSuccessAt?: string;
  error?: string | null;
}

/**
 * Where a GAM order sits in its lifecycle (mirrors backend
 * types/advertiserReports.AdvertiserCampaignLifecycle):
 *  - in_work   — DRAFT / PENDING_APPROVAL (admin-only, never auto-created)
 *  - live      — ≥1 DELIVERING / DELIVERY_EXTENDED line
 *  - upcoming  — approved, not started (or started with only READY lines)
 *  - paused    — started, not ended, remaining lines only PAUSED* / INACTIVE
 *  - completed — flight ended, or every counted line COMPLETED
 */
export type AdvertiserCampaignLifecycle = 'in_work' | 'live' | 'upcoming' | 'paused' | 'completed';

/** Report-window KPI totals mirrored onto the page doc (advertiser hub cards). Rates are fractions. */
export interface CampaignReportKpiSummary {
  totalImpressions: number;
  totalClicks: number;
  ctr: number;
  completionRate: number | null;
  viewabilityRate: number;
  totalSpendUsd: number;
  reportEndDate: string;
  generatedAt: string;
}

/** Compact pacing block mirrored onto the page doc. Percent fields are 0–100 numbers. */
export interface CampaignReportPacingSummary {
  status: GamPacingStatus;
  progressPercent: number;
  deliveryPercent: number;
  expectedDeliveryPercent: number | null;
  impressionsGoal: number;
  impressionsDelivered: number;
  impressionsDeliveredAllLines: number | null;
  reportWindowIsFullFlight: boolean;
  asOf: string;
}

export interface CampaignReportPage {
  slug: string;
  gamOrderId: string;
  gamOrderName: string;
  advertiserId: string;
  advertiserName: string;
  clientLabel: string;
  /** Verbatim-validated brand names derived at creation ([] = none found); admin-editable (max 3). */
  brandNames?: string[];
  flightStartDate: string;
  flightEndDate: string;
  reportStartDate: string;
  requestedEndDate: string;
  reportingWindowMode?: ReportWindowMode;
  windowRevision?: number;
  expiryOverridden?: boolean;
  settlementEndDate?: string;
  refreshHealth?: Partial<Record<'headline' | 'delivery' | 'dates' | 'daily' | 'devices' | 'reach' | 'order', RefreshSectionHealth>>;
  headlineDelivery?: {
    impressions: number; clicks: number; ctr: number;
    source: 'gam_order' | 'gam_report'; scope: 'lifetime' | 'report_window'; capturedAt: string;
    startDate?: string; endDate?: string;
  };
  reportEndDate: string;
  finalized: boolean;
  autoRefresh: boolean;
  /** Client surfaces show delivered-to-date only — no booked goal, no pacing verdict (absent = false). */
  hideBookedGoal?: boolean;
  expiresAt: string;
  pin: string;
  pinUpdatedAt: string;
  pinUpdatedBy: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
  revokedAt: string | null;
  revokedBy?: string;
  lastAccessAt: string | null;
  accessCount: number;
  failedPinAttempts: number;
  lockedUntil: string | null;
  dataStatus: CampaignReportDataStatus;
  dataError?: string;
  lastRefreshStartedAt?: string;
  lastRefreshAt?: string;
  /** Delivery pacing captured at the last generation (null = unavailable). */
  pacingStatus?: GamPacingStatus | null;
  /** % of expected pace at last generation (0–100+). */
  pacingProgressPercent?: number | null;
  /** ISO moment of the last pacing capture (generation or hourly refresh). */
  pacingAsOf?: string | null;
  /** Advertiser hub (advertiserReportPages slug) this page is listed on; null/absent = standalone. */
  advertiserPageSlug?: string | null;
  /**
   * 'advertiser-sync' = auto-created by an advertiser page sync, so the client
   * may only ever have held the ADVERTISER passcode. Absent on legacy docs (= 'admin').
   */
  createdVia?: 'admin' | 'advertiser-sync';
  kpiSummary?: CampaignReportKpiSummary | null;
  pacingSummary?: CampaignReportPacingSummary | null;
  /** GAM lifecycle bucket captured by the advertiser sync (absent → derived server-side). */
  lifecycle?: AdvertiserCampaignLifecycle | null;
  lifecycleAsOf?: string | null;
  /** Computed by the list endpoint. */
  expired?: boolean;
  generating?: boolean;
}

/** One GAM order in the campaign picker (backend campaignOrderPickerService.OrderPickerRow). */
export interface ReportableOrder {
  gamOrderId: string;
  gamOrderName: string;
  advertiserId: string;
  advertiserName: string;
  startDate: string;
  /** '' when the order has no end date (unlimited flight). */
  endDate: string;
  /** Legacy two-state flag: live → DELIVERING, everything else → ENDED. Prefer `lifecycle`. */
  status: 'DELIVERING' | 'ENDED';
  lifecycle: AdvertiserCampaignLifecycle;
  lineItemCount?: number;
  totalImpressionsDelivered?: number;
  /** Non-revoked page already covering this order, if any. */
  existingPageSlug: string | null;
  /** Advertiser hub that existing page is linked to, if any. */
  advertiserPageSlug: string | null;
  /** Living advertiser hub covering this order's advertiser (or including this order), if any. */
  existingAdvertiserPageSlug: string | null;
  /** Living advertiser hub whose gamAdvertiserIds cover this order's ADVERTISER — never via a manual include. */
  advertiserHubSlug?: string | null;
}

export interface BrandPreview {
  /** Brand names found verbatim in the GAM order/line-item metadata ([] = none). */
  brands: string[];
  /** Brands joined for display, or the advertiser name when none were found. */
  suggestedLabel: string;
  advertiserName: string;
}

export interface CreateCampaignReportPageInput {
  gamOrderId: string;
  slug?: string;
  pin?: string;
  clientLabel?: string;
  reportStartDate?: string;
  requestedEndDate?: string;
  reportingWindowMode?: ReportWindowMode;
  /** Advertiser hub to list the page on (must be live and cover the order). */
  advertiserPageSlug?: string;
}

export interface UpdateCampaignReportPageInput {
  clientLabel?: string;
  autoRefresh?: boolean;
  reportingWindowMode?: ReportWindowMode;
  reportStartDate?: string;
  requestedEndDate?: string;
  /** Hide the booked goal on the client page, XLSX and hub card (delivered-to-date only). */
  hideBookedGoal?: boolean;
  expiresAt?: string;
  /** Admin correction of mis-derived brands (max 3, each ≤ 40 chars). */
  brandNames?: string[];
  /** Link to a hub, or null to unlink. */
  advertiserPageSlug?: string | null;
}

export const getReportableOrders = async (
  query?: string,
  force?: boolean,
): Promise<{ orders: ReportableOrder[]; fetchedAt: string }> => {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (force) params.set('force', '1');
  const qs = params.toString();
  return apiRequest(`${BASE}/orders${qs ? `?${qs}` : ''}`);
};

export const getBrandPreview = async (gamOrderId: string): Promise<BrandPreview> =>
  apiRequest(`${BASE}/brand-preview?gamOrderId=${encodeURIComponent(gamOrderId)}`);

export const createCampaignReportPage = async (
  input: CreateCampaignReportPageInput,
): Promise<{ page: CampaignReportPage; url: string }> =>
  apiRequest(BASE, { method: 'POST', body: JSON.stringify(input) });

export const listCampaignReportPages = async (): Promise<CampaignReportPage[]> => {
  const body = await apiRequest(BASE);
  return body.pages ?? [];
};

export const refreshCampaignReportPage = async (slug: string): Promise<CampaignReportPage> => {
  const body = await apiRequest(`${BASE}/${encodeURIComponent(slug)}/refresh`, { method: 'POST' });
  return body.page;
};

export const updateCampaignReportPage = async (
  slug: string,
  patch: UpdateCampaignReportPageInput,
): Promise<CampaignReportPage> => {
  const body = await apiRequest(`${BASE}/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return body.page;
};

export const resetCampaignReportPagePin = async (slug: string, pin?: string): Promise<CampaignReportPage> => {
  const body = await apiRequest(`${BASE}/${encodeURIComponent(slug)}/reset-pin`, {
    method: 'POST',
    body: JSON.stringify(pin ? { pin } : {}),
  });
  return body.page;
};

export const revokeCampaignReportPage = async (slug: string): Promise<CampaignReportPage> => {
  const body = await apiRequest(`${BASE}/${encodeURIComponent(slug)}/revoke`, { method: 'POST' });
  return body.page;
};

export const restoreCampaignReportPage = async (slug: string): Promise<CampaignReportPage> => {
  const body = await apiRequest(`${BASE}/${encodeURIComponent(slug)}/restore`, { method: 'POST' });
  return body.page;
};

export const deleteCampaignReportPage = async (slug: string): Promise<void> => {
  await apiRequest(`${BASE}/${encodeURIComponent(slug)}`, { method: 'DELETE' });
};
